import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useCompanyFilter } from "@/lib/companyFilter";
import {
  BarChart3,
  FileSpreadsheet,
  FileText,
  Filter,
  Calendar,
  RefreshCw,
} from "lucide-react";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const inputCls =
  "px-3 py-2 text-sm border border-slate-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4]";

export default function LeaveReports() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(""); // "" = full year
  const [departmentId, setDepartmentId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [leaveTypeId, setLeaveTypeId] = useState("");
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const { companyId, companyOptions, setCompanyId, empParams } = useCompanyFilter();

  const loadFilters = async () => {
    const [d, e] = await Promise.all([
      api.get("/departments"),
      api.get("/employees", { params: empParams }),
    ]);
    setDepartments(d.data);
    setEmployees(e.data);
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (month) params.month = parseInt(month);
      if (departmentId) params.department_id = departmentId;
      if (employeeId) params.employee_id = employeeId;
      if (leaveTypeId) params.leave_type_id = leaveTypeId;
      if (companyId && companyId !== "all") params.company_id = companyId;
      const r = await api.get("/leaves/report", { params });
      setReport(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line
  }, [companyId]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line
  }, [year, month, departmentId, employeeId, leaveTypeId, companyId]);

  const filteredEmployees = useMemo(() => {
    if (!departmentId) return employees;
    return employees.filter((e) => e.employment.department_id === departmentId);
  }, [employees, departmentId]);

  const downloadFile = async (fmt) => {
    const params = new URLSearchParams({ year: String(year) });
    if (month) params.set("month", month);
    if (departmentId) params.set("department_id", departmentId);
    if (employeeId) params.set("employee_id", employeeId);
    if (leaveTypeId) params.set("leave_type_id", leaveTypeId);
    if (companyId && companyId !== "all") params.set("company_id", companyId);
    const res = await api.get(`/exports/leave-report.${fmt}?${params.toString()}`, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `leave_report_${year}${month ? "_" + String(month).padStart(2, "0") : ""}.${fmt}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <AdminLayout title="Leave Reports" breadcrumbs={["Home", "HR", "Leave Reports"]}>
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded shadow-sm mb-4">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 text-slate-700">
          <Filter className="h-4 w-4 text-[#226DB4]" />
          <span className="font-semibold text-sm">Filters</span>
        </div>
        <div className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Company</label>
            <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setEmployeeId(""); }} className={inputCls} data-testid="lr-company-select">
              {companyOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Year</label>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className={inputCls} data-testid="lr-year-select">
              {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Month (optional)</label>
            <select value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} data-testid="lr-month-select">
              <option value="">Full Year</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Leave Type</label>
            <select value={leaveTypeId} onChange={(e) => setLeaveTypeId(e.target.value)} className={inputCls} data-testid="lr-type-select">
              <option value="">All Types</option>
              {(report?.leave_types || []).map((lt) => (
                <option key={lt.id} value={lt.id}>{lt.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Department</label>
            <select
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
                setEmployeeId("");
              }}
              className={inputCls}
              data-testid="lr-dept-select"
            >
              <option value="">All Departments</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Employee</label>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={inputCls} data-testid="lr-emp-select">
              <option value="">All Employees</option>
              {filteredEmployees.map((e) => (
                <option key={e.id} value={e.id}>{e.basic.code} — {[e.basic.first_name, e.basic.middle_name, e.basic.last_name].filter(Boolean).join(" ")}</option>
              ))}
            </select>
          </div>
          <button
            onClick={loadReport}
            className="px-3 py-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="lr-export-xlsx-btn"
              onClick={() => downloadFile("xlsx")}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 text-slate-700"
            >
              <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
            </button>
            <button
              data-testid="lr-export-pdf-btn"
              onClick={() => downloadFile("pdf")}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 text-slate-700"
            >
              <FileText className="h-4 w-4 text-rose-600" /> PDF
            </button>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total Approved Requests"
          value={report?.total_requests ?? 0}
          icon={<Calendar className="h-5 w-5" />}
          color="#226DB4"
          testid="lr-stat-requests"
        />
        <StatCard
          label="Total Leave Days"
          value={report?.total_days ?? 0}
          icon={<BarChart3 className="h-5 w-5" />}
          color="#089949"
          testid="lr-stat-days"
        />
        <StatCard
          label="Pending Requests"
          value={report?.pending_requests ?? 0}
          icon={<Calendar className="h-5 w-5" />}
          color="#f39c12"
          testid="lr-stat-pending"
        />
        <StatCard
          label="Leave Types"
          value={report?.by_type?.length ?? 0}
          icon={<BarChart3 className="h-5 w-5" />}
          color="#605ca8"
          testid="lr-stat-types"
        />
      </div>

      {loading && <div className="text-slate-500 text-sm mb-4">Loading report…</div>}

      {/* By Type */}
      <div className="bg-white border border-slate-200 rounded shadow-sm mb-5">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800">By Leave Type</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Type</th>
              <th className="text-left px-4 py-3 font-semibold">Code</th>
              <th className="text-right px-4 py-3 font-semibold">Requests</th>
              <th className="text-right px-4 py-3 font-semibold">Employees</th>
              <th className="text-right px-4 py-3 font-semibold">Total Days</th>
              <th className="px-4 py-3 font-semibold">Distribution</th>
            </tr>
          </thead>
          <tbody>
            {(report?.by_type || []).length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="lr-type-empty">No data for the selected filters</td></tr>
            ) : (
              report.by_type.map((t) => {
                const pct = report.total_days ? Math.round((t.total_days / report.total_days) * 100) : 0;
                return (
                  <tr key={t.leave_type_id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2">
                      <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.name}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{t.code}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{t.requests}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{t.unique_employees}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">{t.total_days}</td>
                    <td className="px-4 py-3 w-64">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                          <div
                            className="h-full rounded transition-all"
                            style={{ width: `${pct}%`, backgroundColor: t.color }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 w-9 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* By Department */}
      <div className="bg-white border border-slate-200 rounded shadow-sm mb-5">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800">By Department</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Department</th>
              <th className="text-right px-4 py-3 font-semibold">Requests</th>
              <th className="text-right px-4 py-3 font-semibold">Employees</th>
              <th className="text-right px-4 py-3 font-semibold">Total Days</th>
              <th className="px-4 py-3 font-semibold">Distribution</th>
            </tr>
          </thead>
          <tbody>
            {(report?.by_department || []).length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400" data-testid="lr-dept-empty">No data</td></tr>
            ) : (
              report.by_department.map((d) => {
                const pct = report.total_days ? Math.round((d.total_days / report.total_days) * 100) : 0;
                return (
                  <tr key={d.department_id || "_"} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-800">{d.department_name}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{d.requests}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{d.unique_employees}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">{d.total_days}</td>
                    <td className="px-4 py-3 w-64">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                          <div className="h-full rounded bg-[#226DB4] transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 w-9 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* By Employee */}
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800">By Employee</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Code</th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Department</th>
                <th className="text-left px-4 py-3 font-semibold">Designation</th>
                {(report?.leave_types || []).map((lt) => (
                  <th key={lt.id} className="text-right px-3 py-3 font-semibold" title={lt.name}>
                    {lt.code}
                  </th>
                ))}
                <th className="text-right px-4 py-3 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {(report?.by_employee || []).length === 0 ? (
                <tr><td colSpan={5 + (report?.leave_types?.length || 0)} className="px-4 py-8 text-center text-slate-400" data-testid="lr-emp-empty">No leave consumption by employees for these filters</td></tr>
              ) : (
                report.by_employee.map((e) => (
                  <tr key={e.employee_id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-700">{e.employee_code}</td>
                    <td className="px-4 py-3 text-slate-800">{e.employee_name}</td>
                    <td className="px-4 py-3 text-slate-600">{e.department}</td>
                    <td className="px-4 py-3 text-slate-600">{e.designation}</td>
                    {(report?.leave_types || []).map((lt) => (
                      <td key={lt.id} className="px-3 py-3 text-right text-slate-700">
                        {e.by_type[lt.code] || 0}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700">{e.total_days}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, color, icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden flex items-stretch"
    >
      <div className="flex items-center justify-center text-white px-5" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="px-4 py-3 flex-1">
        <div className="text-2xl font-bold text-slate-800 leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-medium">{label}</div>
      </div>
    </div>
  );
}
